import { useEffect, useMemo, useState } from 'react';
import sortBy from 'lodash/sortBy';
import { PRODUCT_DISCOVERY_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { trackFetchErrors } from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import type { DeepDiveInfo, DeepDiveItem, Project } from '../../../../common/types.tsx';
import {
	createProjectAri,
	getProductDiscoveryProjectUrl,
	getViewUrl,
} from '../../../../common/utils.tsx';
import type { PolarisProject, View, ViewSet, ViewsQueryResponse } from './types.tsx';

export const operationName = 'jira_polaris_getDeepDiveProductDiscoveryViews';

export const viewQuery = `
query ${operationName}($ari: ID!) {
  polarisProject(id: $ari, skipRefresh: true) {
    key
    viewsets {
      ...ViewSet
      viewsets {
        ...ViewSet
      }
    }
  }
}

fragment ViewSet on PolarisViewSet {
  type
  name
  rank
  views {
    id
    rank
    name
    xid
  }
}
`;

const fetchProductDiscoveryViews = (cloudid: string, id: number): Promise<ViewsQueryResponse> => {
	const queryUrl = '/gateway/api/graphql';
	const ari = createProjectAri(cloudid, id.toString());
	return fetchJson(queryUrl, {
		method: 'POST',
		body: JSON.stringify({
			operationName,
			query: viewQuery,
			variables: {
				ari,
			},
		}),
		headers: {
			'X-ExperimentalApi': 'polaris-v0',
		},
	});
};

type ViewResults = {
	data?: {
		[project: string]: DeepDiveItem;
	};
};

const initialViewsState = (productDiscoveryProjects: Project[]) => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const result: Record<string, any> = {};
	productDiscoveryProjects.forEach(({ id: projectId }) => {
		result[projectId] = {
			loading: true,
			error: false,
		};
	});
	return result;
};

export const viewSetsToDeepDiveInfo = (polarisProject: PolarisProject): DeepDiveInfo[] => {
	const { key, viewsets = [] } = polarisProject;
	const priorityViewSet = viewsets?.find(({ type }) => type === 'PRIORITIZE');

	if (priorityViewSet) {
		const flattenedViewsAndViewSets: (ViewSet | View)[] = [
			...(priorityViewSet.views ?? []),
			...(priorityViewSet.viewsets ?? []),
		];

		const topLevelViewsSorted = sortBy(
			flattenedViewsAndViewSets,
			(viewOrViewSet) => viewOrViewSet.rank,
		);

		return topLevelViewsSorted.map((viewOrViewSet) => {
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			if ((viewOrViewSet as ViewSet).type !== undefined) {
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				const viewSet = viewOrViewSet as ViewSet;
				const viewsInfo =
					viewSet?.views?.map<DeepDiveInfo>(({ name, xid }) => ({
						type: 'productDiscoveryView',
						content: name,
						href: getViewUrl(key, xid),
					})) ?? [];
				return {
					type: 'productDiscoveryView',
					content: viewSet.name,
					href: getProductDiscoveryProjectUrl(key),
					children: viewsInfo,
				};
			}

			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			const view = viewOrViewSet as View;
			return {
				type: 'productDiscoveryView',
				content: view.name,
				href: getViewUrl(key, view.xid),
			};
		});
	}

	return [];
};

const useFetchProductDiscoveryViews = (recentProjects: Project[]) => {
	const { cloudId } = useTenantContext();
	const productDiscoveryProjects = useMemo(
		() => recentProjects.filter((project) => project.projectType === PRODUCT_DISCOVERY_PROJECT),
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[JSON.stringify(recentProjects)],
	);
	const [results, setResults] = useState<ViewResults>({
		data: initialViewsState(productDiscoveryProjects),
	});

	useEffect(() => {
		let didCancel = false;
		setResults({ data: initialViewsState(productDiscoveryProjects) });

		(async () => {
			await Promise.all(
				productDiscoveryProjects.map(async ({ id: projectId }) => {
					try {
						const { data: viewData } = await fetchProductDiscoveryViews(cloudId, projectId);

						!didCancel &&
							setResults(({ data }) => ({
								data: {
									...data,
									[projectId]: {
										items: viewData.polarisProject
											? viewSetsToDeepDiveInfo(viewData.polarisProject)
											: [], // Empty if polarisProject is null (deleted)
										loading: false,
										error: false,
									},
								},
							}));
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
					} catch (fetchError: any) {
						trackFetchErrors({
							error: fetchError,
							id: 'fetchProductDiscoveryViews',

							packageName: 'deepDiveCards',
						});
						!didCancel &&
							setResults(({ data }) => ({
								data: {
									...data,
									[projectId]: {
										loading: false,
										error: true,
									},
								},
							}));
					}
				}),
			);
		})();
		return () => {
			didCancel = true;
		};
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cloudId, JSON.stringify(recentProjects)]);

	return results;
};

export default useFetchProductDiscoveryViews;
